/* ───────────────────────────────────────────────────────────────────────────
 * minimalLcsDecisionProfiles.js
 *
 * PROTOTYPE — minimal "operator intelligence console" right-panel format.
 * Hand-authored for the top 5 LCS leases only:
 *   CARTERBF, CHADR288, GILLUMA, NORTHW, CRICKETSU
 *
 * Toggle in ForecastDashboardDecisionUI.js: USE_MINIMAL_DECISION_PANEL.
 * Set to false to fall back to the existing DecisionCard for these leases.
 *
 * Schema:
 *   leaseId, leaseName,
 *   mode,                  short label e.g. "OPTIMIZE", "INVESTIGATE", "MONITOR"
 *   priority, confidence, freshness   metadata (rendered subtly in footer)
 *   primaryMove,           large headline action
 *   diagnosisLine,         one sentence
 *   stakes: { value, lever, caveat }
 *   evidenceCards: [ { title, main, detail, tone } ]   max 3 visible
 *   nextMove: { primary, secondary, recheck }
 *   detailDrawer: { production, notes, ledger }       collapsed by default
 *
 * Tone values: "positive" | "caution" | "negative" | "neutral"
 * ──────────────────────────────────────────────────────────────────────── */

export const MINIMAL_LCS_DECISION_PROFILES = {
  CARTERBF: {
    leaseId: "CARTERBF",
    leaseName: "CARTER, B.F.",
    mode: "OPTIMIZE",
    priority: "MEDIUM",
    confidence: "HIGH",
    freshness: "FRESH",

    primaryMove: "Review water + chemical program",

    diagnosisLine:
      "March gas-handling issue appears resolved; CARTERBF is now stable and the live lever is water/chemical cost control.",

    whyItMatters:
      "Means gas issue likely resolved; economics can now focus on water cost.",

    inlineProof: "Gas MAPE 65→12 · Water p95 393→217 · Forecast fresh",

    stakes: {
      value: "$3k–$9k/mo directional",
      lever: "Water disposal + chemical program",
      caveat: "Water model remains noisy; stage changes before committing.",
    },

    evidenceCards: [
      {
        title: "Gas stabilized",
        main: "MAPE 65% → 12%",
        detail: "Trend reversed -22% → +1%; surface fix appears to have held.",
        tone: "positive",
      },
      {
        title: "Water improved",
        main: "p95 393 → 217 bbl/d",
        detail: "Still volatile (MAPE 300%) but exposure is materially lower.",
        tone: "caution",
      },
      {
        title: "Forecast fresh",
        main: "0d (refreshed today)",
        detail: "Dollar sizing is back on the table — directional only.",
        tone: "positive",
      },
    ],

    nextMove: {
      primary: "Pull 60d water-disposal tickets / SWD terms",
      secondary: "Audit chemical program against current oil rate",
      recheck: "14d",
    },

    detailDrawer: {
      production: "Oil ~27 bbl/d stable; gas ~41 mcf/d clean (MAPE 12%); water p95 ~217 bbl/d, MAPE still 300%.",
      notes:
        "03-07 pumper note: 'gas line at 57 psi, two-phase swamped, knockout same pressure'. The fix appears to have held — gas trend is back to flat.",
      ledger:
        "Recommendation: PROPOSED. Tracking starts today. Outcome pending. Recheck 14d. Watch: water disposal cost vs. deck, oil trend stability.",
    },
  },

  CHADR288: {
    leaseId: "CHADR288",
    leaseName: "CHADBOURNE RANCH -288-",
    mode: "MONITOR",
    priority: "LOW",
    confidence: "MEDIUM",
    freshness: "FRESH",

    primaryMove: "Monitor — do not over-optimize routine notes",

    diagnosisLine:
      "Recent notes read as routine haul/load paperwork rather than a field failure; keep on watch unless production breaks trend.",

    whyItMatters:
      "Notes are paperwork, not field events; do not over-spend.",

    inlineProof: "Notes routine · Oil ~9 bbl/d stable · Forecast fresh",

    stakes: {
      value: "No immediate spend recommended",
      lever: "Avoid unnecessary field optimization",
      caveat: "If notes shift from routine to diagnostic (e.g. equipment events), reclassify.",
    },

    evidenceCards: [
      {
        title: "Notes routine",
        main: "Mostly haul/load tickets",
        detail: "No equipment events; not strong diagnostic signal.",
        tone: "neutral",
      },
      {
        title: "Production stable",
        main: "Oil p50 ~9 bbl/d, gas ~32 mcf/d",
        detail: "Within model. Gas at MAPE 8% is the cleanest stream.",
        tone: "positive",
      },
      {
        title: "Forecast fresh",
        main: "0d (refreshed today)",
        detail: "No staleness gating any decision.",
        tone: "positive",
      },
    ],

    nextMove: {
      primary: "Keep on monitor list",
      secondary: "Recheck if notes shift from routine to diagnostic",
      recheck: "14–30d",
    },

    detailDrawer: {
      production: "Oil ~9 bbl/d on a recent_level fit; gas tight at ~32 mcf/d (MAPE 8%); water sparse (4 positives).",
      notes: "Recent notes are 'Called in a load' / 'Switched to tank #2' — routine haul cycle, no equipment events.",
      ledger: "Recommendation: NO ACTION / MONITOR. Tracking starts today. Recheck 30d.",
    },
  },

  GILLUMA: {
    leaseId: "GILLUMA",
    leaseName: "GILLUM A",
    mode: "INVESTIGATE",
    priority: "HIGH",
    confidence: "HIGH",
    freshness: "FRESH",

    primaryMove: "Escalate 2R to integrity review",

    diagnosisLine:
      "Repeated flow-line leak repairs across consecutive days suggest a pattern, not a one-off — treat as integrity/workover territory before optimization.",

    whyItMatters:
      "Repeated leak suggests integrity issue, not a single repair.",

    inlineProof: "Leak ×3 days · Water trend +32% · Forecast fresh",

    stakes: {
      value: "$1.1k–$3.3k/mo avoided loss if line holds",
      lever: "Prevent repeated leak downtime / contaminated readings",
      caveat: "If a fourth leak event logs, escalate to formal workover.",
    },

    evidenceCards: [
      {
        title: "Leak pattern",
        main: "3 consecutive days (04-27/28/29)",
        detail: "Same well (2R) — pattern, not a single repair.",
        tone: "negative",
      },
      {
        title: "Water elevated",
        main: "p95 140 bbl/d, trend +32%",
        detail: "Volatility consistent with an active integrity issue.",
        tone: "caution",
      },
      {
        title: "Forecast fresh",
        main: "0d (refreshed today)",
        detail: "Diagnosis stands on the field-note pattern alone.",
        tone: "positive",
      },
    ],

    nextMove: {
      primary: "Escalate 2R to integrity review",
      secondary: "Hold optimization spend until line holds 7 leak-free days",
      recheck: "7d",
    },

    detailDrawer: {
      production: "Oil ~9.3 bbl/d (MAPE 8%, but pre-event); gas inactive; water p95 ~140 bbl/d, MAPE 89%.",
      notes:
        "04-27, 04-28, 04-29: '2R off due to flow line leak. Back on'. Three same-day repairs in a row.",
      ledger:
        "Recommendation: PENDING FIELD REVIEW. Tracking starts today. Recheck 7d. Watch: 4th leak event, water volatility decay.",
    },
  },

  NORTHW: {
    leaseId: "NORTHW",
    leaseName: "NORTH WICKHAM",
    mode: "INVESTIGATE",
    priority: "HIGH",
    confidence: "HIGH",
    freshness: "FRESH",

    primaryMove: "Pull #2 belt assembly",

    diagnosisLine:
      "Belt failures recurring without rain while oil drops sharply — points to mechanical/sheave alignment, not weather downtime.",

    whyItMatters:
      "Mechanical failure pattern is suppressing top-rate producer in the set.",

    inlineProof: "No-rain belt failures ×4 · Oil trend -24% · Water -76%",

    stakes: {
      value: "$11.7k–$29.4k/mo avoided loss",
      lever: "Restore Wickham #2 uptime",
      caveat: "Estimate assumes restored steady-state production days (~56 bbl/d × $70).",
    },

    evidenceCards: [
      {
        title: "Mechanical pattern",
        main: "4 days no-rain failures (04-30 → 05-03)",
        detail: "April rain-attributed failures became no-rain repeats — mechanical, not weather.",
        tone: "negative",
      },
      {
        title: "Production impact",
        main: "Oil trend -1% → -24%",
        detail: "Sharp recent drop consistent with downtime, not reservoir.",
        tone: "negative",
      },
      {
        title: "Well-off pattern",
        main: "Water trend -76% 7v30",
        detail: "Produced-water collapse confirms wells are off, not declining.",
        tone: "caution",
      },
    ],

    nextMove: {
      primary: "Inspect Wickham #2 belt assembly + drive train",
      secondary: "Reconcile Tank 01 attribution with Wickham #3",
      recheck: "7d stable",
    },

    detailDrawer: {
      production: "Oil p50 ~56 bbl/d (highest in set, MAPE 21%); gas inactive; water p95 ~274 bbl/d (MAPE 164%).",
      notes:
        "04-30 → 05-03: 'Belts burn #2' on consecutive days, no rain context. April rain-driven failures (04-18/19) preceded.",
      ledger:
        "Recommendation: PENDING FIELD REVIEW. Tracking starts today. Recheck 7d. Watch: belt assembly status, oil-trend recovery.",
    },
  },

  CRICKETSU: {
    leaseId: "CRICKETSU",
    leaseName: "CRICKET (STRAWN) UNIT",
    mode: "REVIEW",
    priority: "MEDIUM",
    confidence: "HIGH",
    freshness: "FRESH",

    primaryMove: "Verify weekend SWD switchback before sizing",

    diagnosisLine:
      "Surface side is in late-stage repair (injection line fixed 04-30, weekend switch back to #6 planned) — wait one clean window before commercial optimization.",

    whyItMatters:
      "Surface side resolving; clean window needed before sizing.",

    inlineProof: "Repair logged 04-30 · Spike unwound +79%→-2% · SWD reroutes",

    stakes: {
      value: "Cannot size yet",
      lever: "Wait for clean window after SWD switchback",
      caveat: "Water MAPE 182%; recent rates were contaminated by water-on-oil-tank events.",
    },

    evidenceCards: [
      {
        title: "Repair logged",
        main: "Injection line fixed 04-30",
        detail: "Switch back to #6 planned for weekend; verify next field cycle.",
        tone: "positive",
      },
      {
        title: "Spike unwound",
        main: "Oil trend +79% → -2%",
        detail: "Earlier 3× spike was contamination (water-on-oil-tank), now corrected.",
        tone: "caution",
      },
      {
        title: "Attribution recent",
        main: "Multiple SWD reroutes #9↔#6",
        detail: "Per-stream rates need a clean ~14-day window before sizing.",
        tone: "caution",
      },
    ],

    nextMove: {
      primary: "Confirm SWD switched back to #6 over weekend",
      secondary: "Hold optimization 14 days post-switchback",
      recheck: "14d clean reads",
    },

    detailDrawer: {
      production: "Oil ~11.7 bbl/d (MAPE 28%); gas inactive; water p95 ~67 bbl/d (MAPE 182%).",
      notes:
        "04-30: 'Injection line repaired. Switch back to #6 weekend.' 04-15 (historical): 'PULL WATER OFF OIL TANK' — explained the earlier 3× spike.",
      ledger:
        "Recommendation: BLOCKED BY DATA QUALITY. Tracking starts today. Recheck 7d. Watch: SWD switchback verified, water MAPE settling.",
    },
  },
};

export default MINIMAL_LCS_DECISION_PROFILES;
